/**
 * Headless-render bridge — lets the `screenshot` tool produce an image in an
 * environment that has no render loop, without the tool knowing who renders it.
 *
 * WHY THIS EXISTS
 * ---------------
 * `screenshot` captures the live canvas through `ScreenshotTools`, which
 * settles on the next rendered frame. Under a NullEngine (the bench, the MCP
 * server) no frame is ever presented, so the tool fast-fails — correctly, since
 * awaiting there parks the process forever. The consequence measured in round 1
 * of the modelling bench: 11 of 11 tasks tried to look at their own scene and
 * every one was refused, which is the single loop the Blender-style results are
 * built on.
 *
 * A host that CAN render (the bench's Playwright page on `e2e/bench-render.html`)
 * registers itself here. The tool then exports the current scene through the
 * product's own export path and hands it over, exactly as `save-bridge.ts` hands
 * a save to whoever can persist it. Nothing auto-registers: with no renderer the
 * tool behaves exactly as it did before.
 */

import type { TraceScene } from 'trace-viewer/scene';

import type { CapturePose } from './ai/capture-pose';

export interface SceneRenderRequest {
    /**
     * The scene as `{ sceneName, version, content }` — the same envelope
     * `--export-scenes` writes and `e2e/load-bench-scene.ts` loads, so what the
     * model is shown is what a user opening the file would see.
     */
    scene: unknown;
    /**
     * The live scene the request came from. The renderer never reads it; it is
     * the identity a harness uses to attribute the render to its task (tasks
     * run concurrently, each with its own scene).
     */
    traceScene: TraceScene;
    /** Viewpoint to shoot from. Null/undefined = the renderer's default view. */
    pose?: CapturePose | null;
    width?: number;
    height?: number;
    /** The author requests the host's reserved post-build review, if supported. */
    finalReview?: boolean;
}

/** Render a scene and resolve with a `data:image/...;base64,...` URL. */
export type SceneRenderer = (request: SceneRenderRequest) => Promise<string>;

let activeRenderer: SceneRenderer | null = null;

/** Register the host that can render scenes. Replaces any prior registration. */
export function registerSceneRenderer(renderer: SceneRenderer): void {
    activeRenderer = renderer;
}

/** Tear down the registration (host shutdown). */
export function unregisterSceneRenderer(): void {
    activeRenderer = null;
}

/** The registered renderer, or null when nothing can render here. */
export function getSceneRenderer(): SceneRenderer | null {
    return activeRenderer;
}
